
import { getAppStore } from '~src/app/AppStore';
import { RefreshSelfServiceScheduleDialog } from '~src/app/content/RefreshSelfServiceScheduleDialog';
import { SessionSiteSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~src/commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { Workbooks } from '~src/content/resources/Workbooks';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ISchedule } from '~src/schedule/types/ISchedule';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { IActionContext } from '~src/ts/helpers/ActionContext';
import { AlertErrorCode, CommonErrorCode, SchedulingErrorCode, WebClientErrorCode } from '~src/ts/helpers/ErrorCodes';
import { L10n } from '~src/ts/L10n/L10n';
import { WorkbookContentType } from '~src/ts/models/ContentTypes';
import { ExtractEncryptionState, IDataSourceBase } from '~src/ts/models/IDataSourceInfo';
import { IRemoteRefreshScheduleInfo } from '~src/ts/models/IRemoteRefreshScheduleInfo';
import { IWorkbookBase, IWorkbookInfo } from '~src/ts/models/IWorkbookInfo';
import { INamedItemInfo } from '~src/ts/models/resourceInfoParts';
import { Set } from '~src/ts/models/Set';
import { ContentUrlForItem } from '~src/ts/services/ContentUrl';
import { CreateDatasourceExtractTaskActionService } from './CreateDatasourceExtractTaskAction';
import { CreateExtractTaskActionService, CreateExtractTasksMode, ICreateExtractTasksScope } from './CreateExtractTaskAction';

export namespace CreateWorkbookExtractTaskActionService {
  export function forWorkbook(workbook: IWorkbookBase, context: IActionContext, allowedMode: CreateExtractTasksMode): Promise<IResult> {
    const workbookSet = Set.singleton(workbook, (wb: IWorkbookInfo) => wb.id);
    return forWorkbooks(workbookSet, context, allowedMode);
  }

  export function forWorkbooks(workbooks: Set<IWorkbookBase>, context: IActionContext, allowedMode: CreateExtractTasksMode): Promise<IResult> {
    const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
    const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
    if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite) {
      return RefreshSelfServiceScheduleDialog.open(CreateExtractTaskActionService.getRefreshSSSDialogParams(workbooks.toArray()));
    }
    const scheduleOnServerPrompt = (workbooks.size() === 1)
        ? Messages.schedulesLabelPromptWorkbook({ WORKBOOK_NAME: L10n.elideIfLong(workbooks.first().name) })
        : Messages.schedulesLabelPromptWorkbooks({ COUNT_LOC: L10n.localizedCount(workbooks.size()) });
    const scheduleOnClientPrompt = '';

    const refreshNowPrompt =
      Messages.extractTasksRefreshNowOnWorkbooksPrompt({
        WORKBOOK_COUNT: workbooks.size(),
        WORKBOOK_NAME: L10n.elideIfLong(workbooks.first().name),
        WORKBOOK_COUNT_LOC: L10n.localizedCount(workbooks.size()),
      });

    return CreateExtractTaskActionService.execute(
      workbooks,
      context,
      scheduleOnServerPrompt,
      scheduleOnClientPrompt,
      (ids: string[], scheduleId: string, type: string) =>
        Workbooks.createExtractTask(ids, scheduleId, type),
      (dummyScope: any, id: string, schedule: IRemoteRefreshScheduleInfo) =>
        { return null; },
      () => Promise.resolve({}),
      refreshNowPrompt,
      (ids: string[], type: string) => Workbooks.refreshExtracts(ids, type),
      (schedule: ISchedule) => workbookErrorDetailFactory(schedule),
      allowedMode
    );
  }

  export function createExtractsForWorkbooks(workbook: INamedItemInfo, datasources: Set<IDataSourceBase>, context: IActionContext,
                  encryptionState: ExtractEncryptionState): Promise<IResult> {
    const scope = <ICreateExtractTasksScope> context.scope.$new();
    return Workbooks.createExtracts([].concat(workbook.id), datasources.keys(), encryptionState === ExtractEncryptionState.encrypted)
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          datasources,
          result,
          {
            success: standardActionSuccessMsg(Messages.extractCreationTasksSuccess),
            error: standardActionErrorMsg(Messages.extractCreationTasksError),
          },
          (errorItem: IErrorItem<IDataSourceBase>) => CreateDatasourceExtractTaskActionService.datasourceErrorDetailFactory(null)(errorItem)
        )
      ).finally(() => scope.$destroy());
  }

  export function workbookErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IWorkbookBase>): IErrorDetail => {
      return {
        itemName: errorItem.item.name,
        itemUrl: ContentUrlForItem(errorItem.item, WorkbookContentType),
        errorMessage: workbookErrorMessageFor(errorItem, schedule)
      };
    };
  }

  export function workbookErrorMessageFor(errorItem: IErrorItem<IWorkbookBase>, schedule: ISchedule): string {
    switch(errorItem.error.code) {
      case WebClientErrorCode.TaskTypeRefreshExtractNotAllowed:
        return Messages.extractTasksWorkbookNoExtract();
      case WebClientErrorCode.TaskTypeIncrementExtractNotAllowed:
        return Messages.extractTasksWorkbookNoIncrementalExtract();
      case SchedulingErrorCode.SchedulingExtractTaskExistsForTarget:
        return Messages.extractTasksRefreshAlreadyExistsForWorkbook({
          SCHEDULE: schedule.name
        });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.extractTasksWorkbookErrorInsufficientPermissions();
      case AlertErrorCode.AlertConnectionlessFailure:
        return Messages.credentialMissedOrWrong();
      case AlertErrorCode.AlertOperationCanceled:
        return Messages.jobCancelled();
    }
    return CreateExtractTaskActionService.extractErrorMessageFor(errorItem, schedule);
  }
}